/**
 * LE RÉSULTAT, CÔTÉ ÉCRAN — lot 101, 18/09/2026.
 *
 * Transforme la lecture de `GET /analyse/resultat` en douze mois de SIG
 * (vue d'ensemble, depuis la balance des comptes) ou en douze mois de marge
 * sur coûts directs (vues par activité et par client, depuis Stafa).
 */

import {
  cumul,
  margeDirecte,
  sigDuMois,
  valeurLigne,
  type ChargeSig,
  type Correspondance,
  type Feuille,
  type LigneSig,
  type MargeDirecte,
  type MoisSig,
  type OrdreSig,
  type PrevueSig,
  type VueDirecte,
} from '@stafa/core';

export interface ObjectifLu {
  readonly mois: string;
  readonly vue: 'ensemble' | 'activite' | 'client';
  readonly cible: string;
  readonly indicateur: Indicateur;
  readonly valeur: number;
}

export type Indicateur = 'ca' | 'ebe' | 'rn' | 'mcd';

export interface ResultatLu {
  readonly aujourdhui: string;
  readonly mois: readonly string[];
  readonly balance: readonly {
    mois: string;
    numero: string;
    libelle: string;
    debits: number;
    credits: number;
  }[];
  readonly derniere_lecture: {
    lance_le: string;
    lance_par: string;
    issue: string;
    lignes: number;
  } | null;
  readonly correspondances: readonly Correspondance[];
  readonly objectifs: readonly ObjectifLu[];
  readonly prevues: readonly PrevueSig[];
  readonly ordres: readonly OrdreSig[];
  readonly charges: readonly ChargeSig[];
  readonly activites: readonly { id: string; nom: string; actif: boolean }[];
}

export type Vue =
  | { readonly sorte: 'ensemble' }
  | { readonly sorte: 'activite'; readonly cle: string }
  | { readonly sorte: 'client'; readonly cle: string };

/** Les douze mois de SIG, plus le cumul. */
export function sigDesMois(l: ResultatLu): { mois: MoisSig[]; cumul: MoisSig } {
  const mois = l.mois.map((m) =>
    sigDuMois(
      m,
      l.balance.filter((b) => b.mois === m),
      m === l.aujourdhui.slice(0, 7) ? l.prevues : [],
      l.correspondances,
    ),
  );
  return { mois, cumul: cumul(mois) };
}

export function margesDesMois(l: ResultatLu, vue: VueDirecte): MargeDirecte[] {
  return l.mois.map((m) => margeDirecte(m, vue, l.ordres, l.charges));
}

/** Les clients qui ont un ordre sur la fenêtre. */
export function clientsDuResultat(l: ResultatLu): { cle: string; nom: string }[] {
  const carte = new Map<string, string>();
  for (const o of l.ordres) carte.set(o.client, o.client_nom);
  return [...carte]
    .map(([cle, nom]) => ({ cle, nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

export function objectifDe(
  objectifs: readonly ObjectifLu[],
  vue: Vue,
  mois: string,
  indicateur: Indicateur,
): number | null {
  const cible = vue.sorte === 'ensemble' ? '' : vue.cle;
  return (
    objectifs.find(
      (o) =>
        o.mois === mois &&
        o.vue === vue.sorte &&
        o.cible === cible &&
        o.indicateur === indicateur,
    )?.valeur ?? null
  );
}

/** Les comptes qu'aucune règle ne rattache, sur toute la fenêtre. */
export function comptesNonRattaches(
  mois: readonly MoisSig[],
): { numero: string; libelle: string; montant: number }[] {
  const carte = new Map<string, { numero: string; libelle: string; montant: number }>();
  for (const m of mois) {
    for (const a of m.apports.non_rattache) {
      const numero = a.cle.replace(/^c:/, '');
      const deja = carte.get(numero);
      carte.set(numero, {
        numero,
        libelle: a.libelle,
        montant: (deja?.montant ?? 0) + a.montant,
      });
    }
  }
  return [...carte.values()].sort((a, b) => a.numero.localeCompare(b.numero));
}

/** La suite des valeurs d'une ligne, mois par mois. */
export function serieDe(mois: readonly MoisSig[], ligne: LigneSig): number[] {
  return mois.map((m) => valeurLigne(ligne, m));
}

export function serieObjectif(
  l: ResultatLu,
  vue: Vue,
  indicateur: Indicateur,
): (number | null)[] {
  return l.mois.map((m) => objectifDe(l.objectifs, vue, m, indicateur));
}

export const NOMS_MOIS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

export function moisCourt(m: string): string {
  return `${NOMS_MOIS[Number(m.slice(5, 7)) - 1] ?? m} ${m.slice(2, 4)}`;
}

export function moisLong(m: string): string {
  const longs = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ];
  return `${longs[Number(m.slice(5, 7)) - 1] ?? m} ${m.slice(0, 4)}`;
}

/** « +12,4 % », « −3 % », « — » quand la base est nulle. */
export function pourcent(valeur: number, base: number | null): string {
  if (base === null || base === 0) return '—';
  const p = ((valeur - base) / Math.abs(base)) * 100;
  const t = Math.abs(p).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
  return `${p > 0 ? '+' : p < 0 ? '−' : ''}${t} %`;
}

export const NOMS_INDICATEURS: Readonly<Record<Indicateur, string>> = {
  ca: 'Chiffre d’affaires',
  ebe: 'EBE',
  rn: 'Résultat net',
  mcd: 'Marge sur coûts directs',
};

/** La feuille d'une ligne quand elle n'est pas un solde (pour rattacher un compte). */
export function feuilleParDefaut(ligne: LigneSig): Feuille | null {
  return ligne.de === undefined ? (ligne.cle as Feuille) : null;
}
